import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  VStack,
  Text,
  HStack
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, CheckIcon, CloseIcon } from '@chakra-ui/icons';

interface UserAccess {
  aviation: boolean;
  insight: boolean;
  guacamole: boolean;
  admin: boolean;
}

interface User {
  slack_id: string;
  email: string;
  name: string;
  access: UserAccess;
}

export function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [newUser, setNewUser] = useState<User>({
    slack_id: '',  
    email: '',
    name: '',
    access: {
      aviation: false,
      insight: false,
      guacamole: false,
      admin: false
    }
  });
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    // Filter users based on search term
    const results = users.filter(user => 
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.name && user.name.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredUsers(results);
    setCurrentPage(1); // Reset to first page when search changes
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/users', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      toast({
        title: 'Error fetching users',
        status: 'error',
        duration: 3000
      });
    }
  };

  const handleSubmit = async () => {
    try {
      
      if (!newUser.email) {
        toast({
          title: 'Email is required',
          status: 'error',
          duration: 3000
        });
        return;
      }

      const response = await fetch('/api/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(newUser)
      });

      if (response.ok) {
        toast({
          title: 'User added successfully',
          description: 'User can now log in with their email',
          status: 'success',
          duration: 3000
        });
        fetchUsers();
        onClose();
        setNewUser({
          slack_id: '',
          email: '',
          name: '',
          access: {
            aviation: false,
            insight: false,
            guacamole: false,
            admin: false
          }
        });
      }
    } catch (error) {
      toast({
        title: 'Error adding user',
        status: 'error',
        duration: 3000
      });
    }
  };

  const updateUserAccess = async (email: string, access: UserAccess) => {
    try {
      const response = await fetch(`/api/users/${email}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ access })
      });

      if (response.ok) {
        toast({
          title: 'User updated successfully',
          status: 'success',
          duration: 3000
        });
        fetchUsers();
      }
    } catch (error) {
      toast({
        title: 'Error updating user',
        status: 'error',
        duration: 3000
      });
    }
  };

  const deleteUser = async (email: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const response = await fetch(`/api/users/${email}`, {
          method: 'DELETE',
          credentials: 'include'
        });

        if (response.ok) {
          toast({
            title: 'User deleted successfully',
            status: 'success',
            duration: 3000
          });
          fetchUsers();
        }
      } catch (error) {
        toast({
          title: 'Error deleting user',
          status: 'error',
          duration: 3000
        });
      }
    }
  };

  // Get current users for pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  // Change page
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  return (
    <Box 
      w="100%" 
      p={8} 
      bg="white" 
      borderRadius="xl" 
      boxShadow="lg"
    >
      <Text fontSize="2xl" fontWeight="bold" mb={6}>User Management</Text>
      
      <HStack spacing={4} mb={6} justify="space-between">
        <Button 
          colorScheme="blue" 
          onClick={onOpen}
          leftIcon={<AddIcon />}
          boxShadow="sm"
        >
          Add New User
        </Button>
        
        <Input
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          maxW="300px"
          bg="white"
        />
      </HStack>

      <Box overflowX="auto">
        <Table variant="simple" width="100%">
          <Thead bg="gray.50">
            <Tr>
              <Th>Email</Th>
              <Th>Name</Th>
              <Th>Aviation</Th>
              <Th>InSight</Th>
              <Th>Guacamole</Th>
              <Th>Admin</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {currentUsers.map((user) => (
              <Tr key={user.email} _hover={{ bg: 'gray.50' }}>
                <Td fontWeight="medium">{user.email}</Td>
                <Td>{user.name || 'Not logged in yet'}</Td>
                <Td>
                  <Checkbox
                    colorScheme="blue"
                    isChecked={user.access.aviation}
                    onChange={(e) => updateUserAccess(user.email, {
                      ...user.access,
                      aviation: e.target.checked
                    })}
                  />
                </Td>
                <Td>
                  <Checkbox
                    colorScheme="blue"
                    isChecked={user.access.insight}
                    onChange={(e) => updateUserAccess(user.email, {
                      ...user.access,
                      insight: e.target.checked
                    })}
                  />
                </Td>
                <Td>
                  <Checkbox
                    colorScheme="blue"
                    isChecked={user.access.guacamole}
                    onChange={(e) => updateUserAccess(user.email, {
                      ...user.access,
                      guacamole: e.target.checked
                    })}
                  />
                </Td>
                <Td>
                  <Checkbox
                    colorScheme="blue"
                    isChecked={user.access.admin}
                    onChange={(e) => updateUserAccess(user.email, {
                      ...user.access,
                      admin: e.target.checked
                    })}
                  />
                </Td>
                <Td>
                  <Button
                    colorScheme="red"
                    size="sm"
                    onClick={() => deleteUser(user.email)}
                    leftIcon={<DeleteIcon />}
                  >
                    Delete
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>

        {filteredUsers.length === 0 && (
          <Box textAlign="center" py={8}>
            <Text color="gray.500">No users found</Text>
          </Box>
        )}
      </Box>
      
      {/* Pagination */}
      {filteredUsers.length > 0 && (
        <HStack spacing={2} mt={6} justify="center">
          <Button
            size="sm"
            onClick={() => paginate(currentPage - 1)}
            isDisabled={currentPage === 1}
          >
            Previous
          </Button>
          
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
            <Button
              key={number}
              size="sm"
              colorScheme={currentPage === number ? "blue" : "gray"}
              onClick={() => paginate(number)}
            >
              {number}
            </Button>
          ))}
          
          <Button
            size="sm"
            onClick={() => paginate(currentPage + 1)}
            isDisabled={currentPage === totalPages}
          >
            Next
          </Button>
        </HStack>
      )}

      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader bg="gray.50" borderTopRadius="md">Add New User</ModalHeader>
          <ModalBody py={6}>
            <VStack spacing={5} align="stretch">
              <FormControl isRequired>
                <FormLabel>Email</FormLabel>
                <Input
                  value={newUser.email}
                  onChange={(e) => setNewUser({
                    ...newUser,
                    email: e.target.value
                  })}
                  placeholder="user@westchestergov.com"
                  boxShadow="sm"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Name (Optional)</FormLabel>
                <Input
                  value={newUser.name}
                  onChange={(e) => setNewUser({
                    ...newUser,
                    name: e.target.value
                  })}
                  placeholder="Will be filled when they first log in"
                  boxShadow="sm"
                />
              </FormControl>
              
              <Box 
                bg="gray.50" 
                p={4} 
                borderRadius="md"
              >
                <Text fontWeight="medium" mb={4}>
                  Permissions
                </Text>
                <VStack spacing={3} align="start">
                  <Checkbox
                    colorScheme="blue"
                    isChecked={newUser.access.aviation}
                    onChange={(e) => setNewUser({
                      ...newUser,
                      access: {
                        ...newUser.access,
                        aviation: e.target.checked
                      }
                    })}
                  >
                    Aviation Access
                  </Checkbox>
                  <Checkbox
                    colorScheme="blue"
                    isChecked={newUser.access.insight}
                    onChange={(e) => setNewUser({
                      ...newUser,
                      access: {
                        ...newUser.access,
                        insight: e.target.checked
                      }
                    })}
                  >
                    InSight Access
                  </Checkbox>
                  <Checkbox
                    colorScheme="blue"
                    isChecked={newUser.access.guacamole}
                    onChange={(e) => setNewUser({
                      ...newUser,
                      access: {
                        ...newUser.access,
                        guacamole: e.target.checked
                      }
                    })}
                  >
                    Guacamole Access
                  </Checkbox>
                  <Checkbox
                    colorScheme="blue"
                    isChecked={newUser.access.admin}
                    onChange={(e) => setNewUser({
                      ...newUser,
                      access: {
                        ...newUser.access,
                        admin: e.target.checked
                      }
                    })}
                  >
                    Admin Access
                  </Checkbox>
                </VStack>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter bg="gray.50" borderBottomRadius="md">
            <Button 
              colorScheme="blue" 
              mr={3} 
              onClick={handleSubmit}
              leftIcon={<CheckIcon />}
            >
              Save
            </Button>
            <Button 
              onClick={onClose}
              leftIcon={<CloseIcon />}
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
} 